<template>
  <cst-page-template class="reports-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page">
      <div class="ds-page__header">
        <h1>Rapportages</h1>
      </div>
      <p class="ds-page__intro">
        Download een rapportage als Excel-bestand of laat hem per e-mail versturen naar de
        e-maillijst die daarvoor is ingericht.
      </p>

      <div class="reports-page__list">
        <article v-for="report in reports" :key="report.name" class="report-card">
          <div class="report-card__head">
            <span class="report-card__icon">
              <svg
                v-if="report.requiresFromDate"
                viewBox="0 0 24 24"
                fill="none"
                width="21"
                height="21"
              >
                <path
                  d="M4 20V4M4 20h16"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
                <rect
                  x="8"
                  y="12"
                  width="3"
                  height="5"
                  rx="1"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <rect
                  x="14"
                  y="8"
                  width="3"
                  height="9"
                  rx="1"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" width="21" height="21">
                <path
                  d="M6 3h7l5 5v12a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M13 3v5h5M8 13h8M8 17h5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            <div class="report-card__text">
              <h2 class="report-card__title">{{ report.label }}</h2>
              <div class="report-card__meta">
                <span class="ds-chip">{{ report.module }}</span>
                <span class="ds-chip ds-chip--code">XLSX</span>
              </div>
            </div>
            <div class="report-card__actions">
              <ds-button
                variant="primary"
                :data-qa="`report-${report.name}-download`"
                :disabled="isBusy(report) || !isValid(report)"
                @click="download(report)"
              >
                <ds-spinner v-if="busy[report.name] === 'download'" inline />
                <svg v-else viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 4v10m0 0l-4-4m4 4l4-4M5 18h14"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ busy[report.name] === 'download' ? 'Downloaden…' : 'Download' }}
              </ds-button>
              <ds-button
                variant="ghost"
                :data-qa="`report-${report.name}-email`"
                :disabled="isBusy(report) || !isValid(report)"
                @click="emailCandidate = report"
              >
                <ds-spinner v-if="busy[report.name] === 'email'" inline />
                <svg v-else viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    stroke-width="1.7"
                  />
                  <path
                    d="M4 7l8 6 8-6"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ busy[report.name] === 'email' ? 'Versturen…' : 'E-mail' }}
              </ds-button>
            </div>
          </div>

          <div v-if="report.requiresFromDate" class="report-card__period">
            <div class="report-card__field">
              <label :for="`report-${report.name}-from-date`">Vanaf datum</label>
              <input
                :id="`report-${report.name}-from-date`"
                v-model="fromDate"
                type="date"
                class="ds-field"
                :data-qa="`report-${report.name}-from-date`"
              />
            </div>
            <span
              class="report-card__hint"
              :class="{ 'report-card__hint--required': !isValid(report) }"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.7" />
                <path
                  d="M12 11v5"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
                <circle cx="12" cy="8" r="1.1" fill="currentColor" />
              </svg>
              {{
                isValid(report)
                  ? 'Het rapport loopt vanaf deze datum tot en met vandaag.'
                  : 'Kies eerst een vanaf datum om dit rapport op te halen.'
              }}
            </span>
          </div>
        </article>
      </div>
    </div>

    <ds-confirm-dialog
      id="report-email-confirmation"
      :show="!!emailCandidate"
      title="Rapportage versturen"
      :body="emailConfirmText"
      confirm-label="Versturen"
      cancel-label="Annuleren"
      @confirm="sendEmail"
      @cancel="emailCandidate = null"
    />
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import ReportsService from '@/services/reportsService'
import { EventBus } from '@/plugins/event-bus'

export default {
  name: 'ReportsPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    DsButton,
    DsSpinner,
    DsConfirmDialog,
  },
  data() {
    return {
      reports: [
        {
          name: 'changeOrderOverview',
          label: 'Dagelijks overzicht dealer change order',
          module: 'Dealer change order',
          requiresFromDate: false,
        },
        {
          name: 'changeOrderTotals',
          label: 'Totaaloverzicht dealer change order',
          module: 'Dealer change order',
          requiresFromDate: false,
        },
        {
          name: 'vwpfsReport',
          label: 'VWPFS matchingsrapport',
          module: 'VWPFS',
          requiresFromDate: true,
        },
        {
          name: 'multiBrandStock',
          label: 'Multimerk voorraadlijst',
          module: 'Voorraad',
          requiresFromDate: false,
        },
      ],
      fromDate: '',
      busy: {},
      emailCandidate: null,
    }
  },
  computed: {
    emailConfirmText() {
      return this.emailCandidate
        ? `Weet je zeker dat je rapportage ${this.emailCandidate.label} wilt versturen per e-mail? Deze wordt verstuurd naar de e-maillijst die voor dit rapport is ingericht, niet naar jezelf.`
        : ''
    },
  },
  methods: {
    isValid(report) {
      return !report.requiresFromDate || !!this.fromDate
    },
    isBusy(report) {
      return !!this.busy[report.name]
    },
    paramsFor(report) {
      return report.requiresFromDate ? { fromDate: this.fromDate } : {}
    },
    async download(report) {
      this.busy = { ...this.busy, [report.name]: 'download' }
      try {
        await ReportsService.download(report.name, this.paramsFor(report))
        EventBus.$emit('notification-success', `Rapportage ${report.label} is gedownload.`)
      } catch {
        EventBus.$emit(
          'notification-warning',
          `Downloaden van rapportage ${report.label} is mislukt.`
        )
      } finally {
        this.busy = { ...this.busy, [report.name]: null }
      }
    },
    async sendEmail() {
      const report = this.emailCandidate
      this.emailCandidate = null
      this.busy = { ...this.busy, [report.name]: 'email' }
      try {
        await ReportsService.email(report.name, this.paramsFor(report))
        EventBus.$emit(
          'notification-success',
          `Rapportage ${report.label} wordt per e-mail verstuurd.`
        )
      } catch {
        EventBus.$emit(
          'notification-warning',
          `Versturen van rapportage ${report.label} is mislukt.`
        )
      } finally {
        this.busy = { ...this.busy, [report.name]: null }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. Everything reusable — the buttons, chips, date field and page
// header — comes from the design system (styles/ds/), and every value below is
// a --ds-* token. See styles/ds/README.md before adding anything here.

.reports-page {
  :deep(.page-content) {
    padding-bottom: var(--ds-space-7);
  }
}

.reports-page__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 880px;
}

.report-card {
  overflow: hidden;
  border: 1px solid var(--ds-color-border);
  border-radius: var(--ds-radius-lg);
  background: var(--ds-color-surface);
  box-shadow: var(--ds-shadow-sm);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: var(--ds-color-brand);
    box-shadow: 0 2px 14px rgba(0, 0, 0, 0.07);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: var(--ds-space-4);
    padding: 18px var(--ds-space-5);
    flex-wrap: wrap;
  }

  &__icon {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border: 1px solid var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-accent-subtle);
    color: var(--ds-color-brand);
  }

  &__text {
    flex: 1 1 260px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__title {
    margin: 0;
    font-size: var(--ds-font-size-md);
    font-weight: 700;
    line-height: 1.3;
    color: var(--ds-color-text);
    text-wrap: pretty;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: var(--ds-space-2);

    :deep(.ds-button) {
      min-width: 132px;
    }
  }

  // Only the report that needs a from-date shows this strip.
  &__period {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
    padding: 14px var(--ds-space-5);
    border-top: 1px solid var(--ds-color-border-subtle);
    background: var(--ds-color-surface-sunken);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--ds-color-text-muted);
    }

    .ds-field {
      width: 190px;
    }
  }

  // Sits on the field baseline as a quiet note, and turns into a bordered
  // warning pill when the date is actually missing.
  &__hint {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    font-size: var(--ds-font-size-sm);
    line-height: 1.35;
    color: var(--ds-color-text-subtle);
    text-wrap: pretty;

    svg {
      flex: 0 0 auto;
      width: 15px;
      height: 15px;
      opacity: 0.75;
    }

    &--required {
      min-height: 0;
      padding: 7px var(--ds-space-3);
      border: 1px solid var(--ds-color-warning-border);
      border-radius: var(--ds-radius-full);
      background: var(--ds-color-warning-bg);
      color: var(--ds-color-warning-text);
      font-weight: 500;

      svg {
        opacity: 1;
      }
    }
  }
}

@media (max-width: 720px) {
  .report-card__head {
    align-items: flex-start;
  }

  .report-card__actions {
    width: 100%;

    :deep(.ds-button) {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
